import React, {useEffect} from 'react'
import {Ellipse, Layer} from 'react-konva'
import {ElementShapeType} from '../../../entities/project'
import {useStateWithRef} from '../../../hooks/useStateWithRef'
import type {KonvaNodeEvents} from 'react-konva'
import {Node as NodeRef, NodeConfig} from 'konva/lib/Node'
import {getRelativePointerPosition} from '../util'

export function EllipseToolLayer({
  onElementDrawn,
}: {
  onElementDrawn: (shape: {type: ElementShapeType; props: NodeConfig}) => void
}) {
  const [ellipseCenter, setEllipseCenter, ellipseCenterRef] = useStateWithRef<{
    x: number
    y: number
  } | null>(null)
  const [dimensions, setDimensions, dimensionsRef] = useStateWithRef<{
    radiusX: number
    radiusY: number
  }>({
    radiusX: 0,
    radiusY: 0,
  })

  const layerRef = React.useRef<NodeRef>(null)

  useEffect(() => {
    const onMouseDown: KonvaNodeEvents['onMouseDown'] = (e) => {
      const {x, y} = getRelativePointerPosition(e.target)
      setEllipseCenter({x, y})
    }

    const onMouseMove: KonvaNodeEvents['onMouseMove'] = (e) => {
      if (!ellipseCenterRef.current) {
        return
      }

      const {x, y} = ellipseCenterRef.current
      const {x: mouseX, y: mouseY} = getRelativePointerPosition(e.target)

      const radiusX = Math.abs(mouseX - x)
      const radiusY = Math.abs(mouseY - y)

      setDimensions({
        radiusX,
        radiusY,
      })
    }

    const onMouseUp: KonvaNodeEvents['onMouseUp'] = (e) => {
      if (!ellipseCenterRef.current) {
        return
      }

      let x1 = ellipseCenterRef.current.x
      let x2 = x1 + dimensionsRef.current.radiusX
      let y1 = ellipseCenterRef.current.y
      let y2 = y1 + dimensionsRef.current.radiusY

      onElementDrawn({
        type: 'ellipse',
        props: {
          x: Math.floor(x1),
          y: Math.floor(y1),
          radiusX: Math.floor(x2 - x1),
          radiusY: Math.floor(y2 - y1),
        },
      })
      setEllipseCenter(null)
    }

    const stage = layerRef.current.getStage()

    stage.on('mousedown', onMouseDown)
    stage.on('mousemove', onMouseMove)
    stage.on('mouseup', onMouseUp)

    return () => {
      if (stage) {
        stage.off('mousedown', onMouseDown)
        stage.off('mousemove', onMouseMove)
        stage.off('mouseup', onMouseUp)
      }
    }
  }, [onElementDrawn])

  return (
    <Layer ref={layerRef as any} opacity={0.7}>
      {ellipseCenter && (
        <Ellipse
          fill="blue"
          x={ellipseCenter.x}
          y={ellipseCenter.y}
          radiusX={dimensions.radiusX}
          radiusY={dimensions.radiusY}
        />
      )}
    </Layer>
  )
}
